import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, Injectable, Injector } from '@angular/core';
import { CalendarApi } from '@fullcalendar/core';

import { DialogService } from 'primeng/dynamicdialog';
import { Subject } from 'rxjs';

import { BaseAssetFragment, BaseProductFragment, FullAvailabilityTemplateFragment, FullCalendarEventFragment, ProphetMetrics , ViewDatesOutput } from '../../generated/graphql.generated';
import { RemoveAvailabilityComponent } from '../availability/remove-availability/remove-availability.component';
import { COLOR_MAP, isColorName } from '../colors';

import {   eventTypeInfoMap, JOB_ROLE_MAP, SM_BREAKPOINT } from '../global.constants';
import { AvailabilityEventComponent } from '../schedules/availability-event/availability-event.component';
import { AvailabilityTemplateEventComponent } from '../schedules/availability-template-event/availability-template-event.component';
import { BookOffEventComponent } from '../schedules/book-off-event/book-off-event.component';
import { ScheduleEventComponent } from '../schedules/schedule-event/schedule-event.component';
import { ScheduleEventHoverComponent } from '../schedules/schedule-event-hover/schedule-event-hover.component';
import { ScheduleResourceLabelComponent } from '../schedules/schedule-resource-label/schedule-resource-label.component';
import { RESOURCE_AREA_WIDTH } from '../schedules/schedule.constants';
import { ExtendedCalendarEventForScheduleFragment, BookingInformation, EventColorInfo } from '../schedules/schedule.interfaces';
import { EVENT_STATUS_ICON_MAP } from '../schedules/schedule.util';
import { ViewDatesCardComponent } from '../schedules/view-dates-card/view-dates-card.component';
import { FCEventHolder, getLocationArrivalAndDepartureTimes } from '../shared/event-location/calendarevent.util';
import { PRODUCT_LABOR_CATEGORY } from '../shared/product-categories/product-categories';
import { ensureUnixSeconds } from '../time';
import { lastNameFirstName } from '../users/users.utils';
import { extractPostalCodeFromAddressLineOne, getFirstLocation, getLocationAreaCode } from '../utilities/locations.util';

import { DetailsHelperService } from './details-helper.service';

import { FreyaHelperService } from './freya-helper.service';


export { ExtendedCalendarEventForScheduleFragment, BookingInformation, EventColorInfo };

@Injectable({
  providedIn: 'root'
})
export class FullCalendarHelperService {

  // Used to set the date of the calendar
  changeCalendarDate = new Subject<Date>();

  availabilityClicked = new Subject();

  private currentHoverComponentRef: ComponentRef<any> | null = null;

  constructor(
    private freyaHelper: FreyaHelperService,
    private detailsHelper: DetailsHelperService,
    private dialogService: DialogService,
    private injector: Injector,
    private appRef: ApplicationRef,
    private environmentInjector: EnvironmentInjector
  ) { }

  createFreyaAvailability(hidden, child = false){
    const componentRef = createComponent(AvailabilityEventComponent, {
      environmentInjector: this.environmentInjector
    });

    const instance = componentRef.instance;
    instance.hidden = hidden;
    instance.child = child;

    componentRef.changeDetectorRef.detectChanges();

    return componentRef.location.nativeElement;
  }

  createViewDatesCard(
    data: ViewDatesOutput | null, showForecast = false, showPacing = false, prophetMetrics: ProphetMetrics | null = null,
  ) {
    const componentRef = createComponent(ViewDatesCardComponent, {
      environmentInjector: this.environmentInjector
    });

    const instance = componentRef.instance;
    instance.data = data;
    instance.showForecast = showForecast;
    instance.showPacing = showPacing;
    instance.prophetMetrics = prophetMetrics;

    componentRef.changeDetectorRef.detectChanges();

    return componentRef.location.nativeElement;
  }

  createFreyaEvent(
    info,
    calendarEvent: ExtendedCalendarEventForScheduleFragment,
    calendar: CalendarApi,
    hidden = false,
    editable = false,
    hasBeenEdited = false,
    disabled = false,
    skipPaymentBorder = false,
    showCrew = false,
    commonCrew: any[] = [],
  ) {
    // If events were moved to this day they will not have a mtaching calendar event in the list
    if (!calendarEvent && info?.event._def?.extendedProps?.loaded === false){
      calendarEvent = info?.event._def?.extendedProps.event;
    }

    // Make sure the calenar event exists
    if (!calendarEvent) {
      // eslint-disable-next-line no-underscore-dangle
      calendar.getEventById(info.event._def.groupId)?.remove();
      return;
    }

    const componentRef = createComponent(ScheduleEventComponent, {
      environmentInjector: this.environmentInjector
    });

    const instance = componentRef.instance;
    instance.event = calendarEvent;
    instance.viewType = calendar.view.type;
    instance.showCrew = showCrew;
    instance.hidden = hidden;
    instance.editable = editable;
    instance.hasBeenEdited = hasBeenEdited;
    instance.disabled = disabled;
    instance.skipPaymentBorder = skipPaymentBorder;
    
    // We pass commonCrew (allDayCrew) to the component, which now handles diff logic and summary
    instance.commonCrew = commonCrew || [];

    componentRef.changeDetectorRef.detectChanges();

    return componentRef.location.nativeElement;
  }

  resolveTitle(calendarEvent: ExtendedCalendarEventForScheduleFragment) {

    const customer = calendarEvent?.job?.users?.find((a) => a.role === JOB_ROLE_MAP.customerRole);

    if (!customer) {
      return calendarEvent.title;
    }

    const startPostalCode = this.getStartPostalCode(calendarEvent);
    const name = customer.user.company?.trim() || lastNameFirstName(customer.user);

    return startPostalCode ? `${name} - ${startPostalCode}` : name;
  }

  getStartPostalCode(calendarEvent: ExtendedCalendarEventForScheduleFragment): string {
    const loc = getFirstLocation(calendarEvent);

    if (loc?.location?.areaCode) {
      return getLocationAreaCode(loc.location);
    }

    // Guard against undefined addressLineOne
    if (loc?.location?.addressLineOne) {
      return extractPostalCodeFromAddressLineOne(loc.location.addressLineOne);
    }

    if (!calendarEvent?.eventIsPlaceholder) {
      return 'No Area';
    }

    return '';
  }

  calculateTimelineSegments(
    calendarEvent: ExtendedCalendarEventForScheduleFragment, 
    fcEvent: any,
    colors: EventColorInfo
  ) {
    if (!fcEvent?.start || !fcEvent?.end || !Number.isFinite(fcEvent.start.getTime()) || !Number.isFinite(fcEvent.end.getTime())) {
      return [];
    }

    const eventStart = ensureUnixSeconds(fcEvent.start.getTime());
    const eventEnd = ensureUnixSeconds(fcEvent.end.getTime());
    const totalDuration = eventEnd - eventStart;
    
    if (totalDuration <= 0) return [];

    const segments = [];
    let currentTime = eventStart;

    const locationsWithTimes = getLocationArrivalAndDepartureTimes(calendarEvent);
    const sortedLocations = [...(locationsWithTimes || [])]
      .sort((a, b) => a.arrivalTime - b.arrivalTime);

    for (const loc of sortedLocations) {
      const zoneColor = (loc.location as any)?.serviceArea?.color || colors.zoneBackgroundColor || '#000000';

      // Travel to location
      if (loc.arrivalTime > currentTime) {
        const travelDuration = loc.arrivalTime - currentTime;
        const width = (travelDuration / totalDuration) * 100;
        if (width > 0) {
          segments.push({
            type: 'travel',
            width,
            color: zoneColor,
            opacity: 0.5,
            title: `Travel: ${(travelDuration / 60).toFixed(0)} min`
          });
        }
      }

      // Work at location
      const departure = Math.min(loc.departureTime, eventEnd); // Cap at event end
      if (departure > loc.arrivalTime) {
        const workDuration = departure - loc.arrivalTime;
        const width = (workDuration / totalDuration) * 100;
        if (width > 0) {
          segments.push({
            type: 'work',
            width,
            color: zoneColor,
            opacity: 1,
            title: `Work: ${(workDuration / 60).toFixed(0)} min`
          });
        }
      }
      
      currentTime = Math.max(currentTime, departure);
    }

    // Final Travel / Remaining
    if (currentTime < eventEnd) {
      // Use last location zone color or default
      const lastLoc = sortedLocations[sortedLocations.length - 1];
      const zoneColor = (lastLoc?.location as any)?.serviceArea?.color || colors.endAreaColor || '#000000';
      
      const remainingDuration = eventEnd - currentTime;
      const width = (remainingDuration / totalDuration) * 100;
      if (width > 0) {
        segments.push({
          type: 'travel',
          width,
          color: zoneColor,
          opacity: 0.5,
          title: `Travel: ${(remainingDuration / 60).toFixed(0)} min`
        });
      }
    }

    return segments;
  }

  createBookedOffEvent(calendarEvent, viewType){
    const componentRef = createComponent(BookOffEventComponent, {
      environmentInjector: this.environmentInjector
    });

    const instance = componentRef.instance;
    instance.event = calendarEvent;
    instance.viewType = viewType;

    componentRef.changeDetectorRef.detectChanges();

    return componentRef.location.nativeElement;
  }

  determineColorsFromEvent(
    ce?: ExtendedCalendarEventForScheduleFragment,
  ): EventColorInfo {
    let eventColorInfo: EventColorInfo = {
      backgroundColor: COLOR_MAP.blue.color,
      textColor: COLOR_MAP.blue.textColor,
      travelTimeColor: COLOR_MAP.blue.lightColor,
    };
    if (!ce) {
      return eventColorInfo;
    }

    const eventTypeInfo = eventTypeInfoMap[ce.type];
    const eventBackgroundColour = ce?.colour || eventTypeInfoMap[ce.type]?.backgroundColor;
    const eventTextColour = ce?.colour || eventTypeInfoMap[ce.type]?.textColor;
    if (eventTypeInfo && (isColorName(eventBackgroundColour))) {
      const color = COLOR_MAP[eventBackgroundColour];
      eventColorInfo.backgroundColor = color.color;
      eventColorInfo.textColor = color.textColor || '#000000';
      eventColorInfo.travelTimeColor = color.lightColor;
    } else if (eventTypeInfo?.backgroundColor) {

    // TODO: validate when we pull this from the server
    eventColorInfo.backgroundColor = eventBackgroundColour;
    eventColorInfo.textColor = eventTextColour || '#000000';
    }

    const products = ce.charges?.map((c) => c.product) || [];
    // todo: sort?
    for (const product of products) {
      const productColorInfo = this.determineColorInfoFromProduct(product);
      if (productColorInfo) {
        eventColorInfo = {
          ...eventColorInfo,
          ...productColorInfo,
        };
        break;
      }
    }

    // Get Colors from service area
    const startLocation = ce.locations?.find((l) => l.type === 'start');
    if (startLocation?.location?.serviceArea) {
      eventColorInfo.zoneBackgroundColor = startLocation?.location?.serviceArea?.color;
      eventColorInfo.zoneTextColor = '#000000';
    } else if (ce.zones?.nodes?.length) {
      const firstZoneWithcolor = ce.zones?.nodes?.find((z) => z.color);
      if (firstZoneWithcolor) {
        eventColorInfo.zoneBackgroundColor = firstZoneWithcolor.color;
        eventColorInfo.zoneTextColor = '#000000';
      } else {
        eventColorInfo.zoneBackgroundColor = 'black';
        eventColorInfo.zoneTextColor = 'white';
      }
    } else {
      // Use black to signify it doesn't match any zonesfirstZoneWithcolor
      eventColorInfo.zoneBackgroundColor = 'black';
    }

    const endLocation = ce.locations?.find((l) => l.type === 'end');
    
    eventColorInfo.endAreaColor = endLocation?.location?.serviceArea?.color 
      ? endLocation.location.serviceArea.color 
      : eventColorInfo.zoneBackgroundColor;

    return eventColorInfo;
  }

  determineColorInfoFromProduct(
    product: Pick<BaseProductFragment, 'id' | 'name' | 'category' | 'metadata'>,
  ) {
    if (!product) { return undefined; }

    if (product?.category !== PRODUCT_LABOR_CATEGORY) { return; }

    const metaColor = product?.metadata?.color;

    if (isColorName(metaColor) && COLOR_MAP[metaColor]) {
      const color = COLOR_MAP[metaColor];
      return {
        leftDivColor: color?.color,
        leftDivTextColor: color?.textColor,
        leftDivColorProduct: product,
      };
    }
    return undefined;
  }

  /**
   * Returns the correct icon class based on event status
   *
   * @param event Calendar event
   * @returns The class for the icon based on the event status
   */
  determineIconFromEvent(event: ExtendedCalendarEventForScheduleFragment): string{
    const icon = EVENT_STATUS_ICON_MAP.find((esi) => esi?.status === event?.status);

    if (!icon && !event?.eventIsPlaceholder){
      return 'pi-bars';
    }

    if (event?.eventIsPlaceholder) {
      return 'pi-sync';
    }

    return icon?.icon;
  }

  removeEventHover() {
    if (this.currentHoverComponentRef) {
      this.currentHoverComponentRef.destroy();
      this.currentHoverComponentRef = null;
    }
    // Also clear any legacy elements just in case
    const overlays = document.querySelectorAll('.freya-fc-hover');
    overlays.forEach(el => el.remove());
  }

  createEventHover(info, event: FullCalendarEventFragment, timezone = undefined){
    this.removeEventHover();

    const colors = this.determineColorsFromEvent(event as any);
    const { eventStart, eventEnd } = this.freyaHelper.getCalendarEventStartandEnd(event);
    
    let locations = [];
    if (event.locations) {
      locations = getLocationArrivalAndDepartureTimes(event);
    }

    const componentRef = createComponent(ScheduleEventHoverComponent, {
      environmentInjector: this.environmentInjector
    });

    const instance = componentRef.instance;
    instance.event = event;
    instance.title = info.event?.title || event?.title || info.title;
    instance.customerName = this.resolveTitle(event);
    instance.colors = colors;
    instance.timezone = timezone;
    instance.currentViewStart = info.view.currentStart;
    instance.currentViewEnd = info.view.currentEnd;
    instance.locations = locations;
    instance.statusIcon = this.determineIconFromEvent(event as any);
    instance.eventStart = eventStart;
    instance.eventEnd = eventEnd;
    instance.crewSize = this.determineCrewSizeFromProduct(colors.leftDivColorProduct);

    componentRef.changeDetectorRef.detectChanges();

    const hoverEl = componentRef.location.nativeElement;
    
    // Initial positioning parameters
    const rect = info.el.getBoundingClientRect();
    const width = rect.width;
    instance.width = width;
    
    hoverEl.style.position = 'absolute';
    hoverEl.style.visibility = 'hidden'; // Hide while positioning
    
    document.body.appendChild(hoverEl);
    
    // Recalculate dimensions after render
    const hoverRect = hoverEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let top = window.scrollY + rect.bottom;
    let left = window.scrollX + rect.left;
    
    // Adjust for Right Edge
    if (rect.left + hoverRect.width > viewportWidth) {
      // Shift left to fit in viewport with some buffer
      const overflowX = (rect.left + hoverRect.width) - viewportWidth;
      left -= (overflowX + 20);
    }
    
    // Adjust for Bottom Edge
    if (rect.bottom + hoverRect.height > viewportHeight) {
      // Shift up above the event if there's no room below
      top = window.scrollY + rect.top - hoverRect.height;
      if (top < window.scrollY + 10) {
        // If there's no room above either, just fit it as best as possible
        top = window.scrollY + viewportHeight - hoverRect.height - 20;
      }
    }
    
    // Ensure not off-screen Top/Left
    top = Math.max(window.scrollY + 10, top);
    left = Math.max(window.scrollX + 10, left);
    
    hoverEl.style.top = `${top}px`;
    hoverEl.style.left = `${left}px`;
    hoverEl.style.visibility = 'visible';
    
    this.currentHoverComponentRef = componentRef;

    return info.el;
  }

  private determineCrewSizeFromProduct(product: any): number | null {
    if (!product) return null;
    
    // Updated logic per feedback: extract first number from name if category is Labor
    // Note: product category check is done before calling this usually, but checking here is safe
    
    // Check regex on name
    const match = product.name?.match(/(\d+)/);
    if (match) {
        return parseInt(match[1], 10);
    }
    
    // Fallback to metadata
    if (product.metadata?.crewSize) {
        const parsed = parseInt(product.metadata.crewSize, 10);
        return Number.isNaN(parsed) ? null : parsed;
    }
    
    return null;
  }

  /**
   * Create the element that is shown in the template row on the availability page
   *
   * @param template Availability Template
   * @param type Type of the application eg. 'Normal' or 'Override'
   * @param overriden True if this template has been overriden by another template
   * @returns Element to show on calendar
   */
  createAvailabilityTemplateEvent(template: FullAvailabilityTemplateFragment, type: string, overriden: boolean){
    const componentRef = createComponent(AvailabilityTemplateEventComponent, {
      environmentInjector: this.environmentInjector
    });

    const instance = componentRef.instance;
    instance.template = template;
    instance.type = type;
    instance.overriden = overriden;

    componentRef.changeDetectorRef.detectChanges();

    return componentRef.location.nativeElement;
  }

  /**
   * Determines the border color based on job payment status
   * Not Paid: Dark Orange (#FF8C00) - Alert/Urgent
   * Partially Paid: Yellow (#FFFF00) - Caution/Transitional  
   * Fully Paid: Green (#008000) - Success/Complete
   * 
   * @param calendarEvent The calendar event containing job financial data
   * @returns The hex color code for the border
   */
  private getPaymentStatusBorderColor(calendarEvent: ExtendedCalendarEventForScheduleFragment): string {
   
    // Only apply borders to job events (not estimating or virtualEstimate)
    if (calendarEvent.type === 'estimating' || calendarEvent.type === 'virtualEstimate') {
      return 'transparent';
    }

    // Default color if no job data
    if (!calendarEvent?.job) {
      return '#FF8C00'; // Default to Not Paid (Dark Orange)
    }

    const job = calendarEvent.job;
    const totalAmount = job.total || 0;
    const paidAmount = job.paidTotal || 0;

    // Fully Paid - Lime Green
    if (paidAmount >= totalAmount && totalAmount > 0) {
      return '#00FF00';
    }

    // Partially Paid - Yellow  
    if (paidAmount > 0 && paidAmount < totalAmount) {
      return '#FFFF00';
    }

    // Not Paid - Crimson Red
    return '#DC143C';
  }

  /**
   * Checks an event for conflict against a list of events
   *
   * @param events The list of events for the given time span
   * @param eventToCheck The event we are checking conflicts for
   * @param editedEvents The local version of the edited events that have not yet been saved
   * @returns True if the event conflicts
   */
  conflictsWithEvent(events: FCEventHolder[], eventToCheck: FCEventHolder, editedEvents: FCEventHolder[]): boolean{
    const start = eventToCheck.start;
    const end = eventToCheck.end;

    for (let event of events){
      // Ignore itself when chcking for conflicts
      if (event.id === eventToCheck.id){
        continue;
      }

      // If the event has been edited, check for conflicts with new version
      const editedVersion = editedEvents.find((edit) => edit.id === event.id);

      if (editedVersion){
        event = editedVersion;
      }

      // If events are not on the same asset ignore conflicts
      if (!event.assetIds.find((a) => eventToCheck.assetIds.includes(a))){
        continue;
      }

      // If the event spans the entirity of the block
      if (event.start <= start && event.end >= end){
          return true;
      }

      // If the event starts during the block and is longer than it
      if ((event.start >= start && event.start <= end) && event.end >= end){
          return true;
      }

      // If the event overlaps the start and ends in the middle of the block
      if (event.start <= start && (event.end <= end && event.end >= start)){
          return true;
      }

      // If the event is in the middle of the block
      if (event.start >= start && event.end <= end){
          return true;
      }
  }
  }


  // RESOURCES
  /**
   * Generate the Resource Label for the timeline view
   *
   * @param info The ResourceLabelContentArg object
   * @param assets List of to search
   * @param showCrew Whether to show crew in resource label
   * @returns HTML for resource label
   */
 generateResourceLabel(info, assets: BaseAssetFragment[], showCrew = false){
    // Get Values from resource
    const title = info.resource._resource.title;
    let assetId = info.resource.id;
    if (assetId.startsWith('A-')){
      assetId = assetId.replace('A-', '');
    }
    const template = info.resource?.extendedProps?.template;
    const allDayCrew = info.resource?.extendedProps?.allDayCrew;

    const matchingAsset = assets?.find((a) => a.id === assetId);

    const componentRef = createComponent(ScheduleResourceLabelComponent, {
      environmentInjector: this.environmentInjector
    });

    const instance = componentRef.instance;
    instance.title = title;
    instance.asset = matchingAsset;
    instance.commonCrew = allDayCrew;
    instance.template = template;
    instance.showCrew = showCrew;

    if (template) {
      instance.remove.subscribe(() => {
        if (this.freyaHelper.inRootOrCorporateZone) {
          return;
        }

        this.dialogService.open(RemoveAvailabilityComponent, {
          header: 'Remove Availability Template',
          data: {
            input: {
              asset: info.resource.extendedProps.parent,
              template,
              type: info.resource.extendedProps.type,
              date: info.resource.extendedProps.date,
            }
          },
          styleClass: 'remove-availability',
          width: '50vw',
        });
      });
    }

    componentRef.changeDetectorRef.detectChanges();

    const element = componentRef.location.nativeElement;

    const nodes = [element];

    return { domNodes: nodes };
}

  getResourceAreaWidth() {
    if (window.innerWidth > SM_BREAKPOINT) {
      return RESOURCE_AREA_WIDTH.md;
    }
    return RESOURCE_AREA_WIDTH.sm;
  }
}
